/**
 * Minutedrone t92 (lot 3) : sélecteur « Choisir une version » entre fiches séparées.
 *
 * Comportement B (décision d'Aymen, 08/10/2026) : un clic sur une carte lit la fiche choisie et remplace le contenu de
 * <main> sans recharger la page (galerie, titre, prix, « Dans la boîte », sections), comme le fait Horizon pour les
 * « combined listings » (variant-picker.js, updateMain). L'adresse est mise à jour par history.pushState ; le retour
 * arrière du navigateur recharge la version précédente de la même façon (popstate) ; l'adresse se partage telle quelle.
 *
 * Repli A : chaque carte est un vrai lien <a href>. Sans ce script, dans l'éditeur de thème, dans une fenêtre d'ajout
 * rapide, avec le réglage « sans rechargement » décoché, avec Ctrl/Cmd/Maj ou le clic du milieu, ou si le chargement
 * échoue, la fiche s'ouvre normalement (rechargement complet).
 *
 * Préchargement : au survol ou au focus d'une carte, la fiche est lue à l'avance (cache 60 s, 6 fiches au plus).
 */
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const CACHE_DUREE_MS = 60000;
const CACHE_MAX = 6;

/** @type {Map<string, { temps: number, promesse: Promise<{ texte: string, url: string }> }>} */
const cache = new Map();

const etat = {
  /** Chemin de la fiche affichée (sert à ignorer les popstate d'ancre ou de variante). */
  chemin: window.location.pathname,
  /** Numéro de la dernière demande : une réponse plus ancienne n'écrase jamais un choix plus récent. */
  jeton: 0,
  ecouteHistorique: false,
};

/**
 * Morph d'Horizon, en gardant l'état affiché/masqué de la barre d'achat collée (data-stuck) :
 * sans cela, la barre se masquerait et ne réapparaîtrait plus (son état interne resterait « collée »).
 */
const OPTIONS_MORPH = {
  ...MORPH_OPTIONS,
  /**
   * @param {Node} ancien
   * @param {Node} nouveau
   */
  onBeforeUpdate(ancien, nouveau) {
    MORPH_OPTIONS.onBeforeUpdate?.(ancien, nouveau);
    if (ancien instanceof Element && nouveau instanceof Element && ancien.hasAttribute('data-stuck')) {
      nouveau.setAttribute('data-stuck', ancien.getAttribute('data-stuck') || 'false');
    }
  },
};

/**
 * Adresse à charger et à afficher : chemin de la carte, paramètres de l'adresse actuelle conservés
 * (aperçu du thème, suivi), sauf « variant » qui appartient à l'ancienne fiche.
 * @param {string} href
 * @returns {URL}
 */
function urlCible(href) {
  const cible = new URL(href, window.location.href);
  const actuelle = new URL(window.location.href);
  actuelle.searchParams.forEach((valeur, cle) => {
    if (cle !== 'variant' && !cible.searchParams.has(cle)) cible.searchParams.set(cle, valeur);
  });
  cible.hash = '';
  return cible;
}

/**
 * Lit une fiche (avec cache court, partagé entre préchargement et clic).
 * @param {URL} url
 * @returns {Promise<{ texte: string, url: string }>}
 */
function lirePage(url) {
  const cle = url.href;
  const entree = cache.get(cle);
  if (entree && Date.now() - entree.temps < CACHE_DUREE_MS) return entree.promesse;

  const promesse = fetch(cle, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(async (reponse) => {
    if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
    return { texte: await reponse.text(), url: reponse.url || cle };
  });

  promesse.catch(() => {
    if (cache.get(cle)?.promesse === promesse) cache.delete(cle);
  });
  cache.delete(cle);
  cache.set(cle, { temps: Date.now(), promesse });
  while (cache.size > CACHE_MAX) {
    const plusAncienne = cache.keys().next().value;
    if (plusAncienne === undefined) break;
    cache.delete(plusAncienne);
  }
  return promesse;
}

/**
 * Le comportement B est-il possible pour ce sélecteur ? Sinon la carte reste un lien simple (repli A).
 * @param {HTMLElement} selecteur
 * @returns {boolean}
 */
function navigationPossible(selecteur) {
  if (/** @type {any} */ (window).Shopify?.designMode) return false;
  if (selecteur.dataset.sansRechargement !== 'true') return false;
  if (typeof history.pushState !== 'function' || typeof DOMParser === 'undefined') return false;
  if (!selecteur.closest('main')) return false;
  if (selecteur.closest('dialog, quick-add-dialog, #quick-add-modal-content')) return false;
  return true;
}

/**
 * Met à jour ce qui, hors de <main>, décrit la fiche : titre de l'onglet, adresse canonique, description.
 * @param {Document} doc
 */
function majEntete(doc) {
  if (doc.title) document.title = doc.title;

  const canonique = doc.querySelector('link[rel="canonical"]');
  const canoniqueActuelle = document.querySelector('link[rel="canonical"]');
  if (canonique && canoniqueActuelle) canoniqueActuelle.setAttribute('href', canonique.getAttribute('href') || '');

  const description = doc.querySelector('meta[name="description"]');
  const descriptionActuelle = document.querySelector('meta[name="description"]');
  if (description && descriptionActuelle) {
    descriptionActuelle.setAttribute('content', description.getAttribute('content') || '');
  }
}

/**
 * Feuilles de style de la nouvelle page absentes de la page actuelle (CSS des blocs rendus seulement sur l'autre fiche).
 * @param {Document} doc
 */
function synchroniserStyles(doc) {
  const presentes = new Set(
    Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'), (lien) => lien.getAttribute('href'))
  );
  for (const lien of doc.querySelectorAll('head link[rel="stylesheet"][href]')) {
    const href = lien.getAttribute('href');
    if (!href || presentes.has(href)) continue;
    document.head.append(lien.cloneNode(true));
    presentes.add(href);
  }
}

/**
 * Les <script type="module"> insérés par morph ne s'exécutent pas : on les importe (le navigateur ne charge
 * jamais deux fois le même module). Les scripts classiques en ligne de <main> ne sont pas rejoués (limite).
 * @param {string[]} sources
 */
function chargerModules(sources) {
  for (const source of sources) {
    import(new URL(source, window.location.href).href).catch((erreur) =>
      console.error('t92 : module non chargé', source, erreur)
    );
  }
}

/**
 * Après le remplacement de <main> : formulaire d'achat, barre collée, bascule HT/TTC, produits vus, focus, annonce.
 * @param {{ produitId: string, varianteId: string, titre: string, avaitFocusDansSelecteur: boolean }} infos
 */
function apresChangement({ produitId, varianteId, titre, avaitFocusDansSelecteur }) {
  const main = document.querySelector('main');
  if (!main) return;

  // Identifiant de variante du formulaire principal (déjà mis à jour par morph ; contrôle explicite).
  const champVariante = main.querySelector('product-form-component input[name="id"]');
  if (champVariante instanceof HTMLInputElement && varianteId && champVariante.value !== varianteId) {
    champVariante.value = varianteId;
  }

  // Barre d'achat collée : Horizon garde en mémoire le bouton « Ajouter au panier » du formulaire lu au chargement,
  // que morph a remplacé (son id contient l'id du produit) ; son bouton ne faisait plus rien (essai du 08/10/2026).
  // Une copie neuve se reconnecte et relit le formulaire affiché ; elle repart masquée et ses observateurs
  // l'affichent aussitôt si les boutons d'achat sont déjà passés. Puis quantité affichée = quantité du formulaire.
  const barre = main.querySelector('sticky-add-to-cart');
  if (barre instanceof HTMLElement) {
    const neuve = /** @type {HTMLElement} */ (barre.cloneNode(true));
    if (produitId) neuve.dataset.productId = produitId;
    if (varianteId) neuve.dataset.currentVariantId = varianteId;
    neuve.querySelector('[ref="stickyBar"]')?.setAttribute('data-stuck', 'false');
    barre.replaceWith(neuve);
  }
  const selecteurQuantite = main.querySelector('product-form-component quantity-selector-component');
  const champQuantite = selecteurQuantite?.querySelector('input[type="number"]');
  if (selecteurQuantite && champQuantite instanceof HTMLInputElement) {
    selecteurQuantite.dispatchEvent(new QuantitySelectorUpdateEvent(parseInt(champQuantite.value, 10) || 1));
  }

  // Bascule HT/TTC (assets/minutedrone-tax-toggle.js) : elle s'initialise sur « shopify:section:load ».
  // Morph retire data-tax-ready : les prix remplacés (fiche, cartes, sous-total) reprennent le mode choisi.
  const section = (champVariante || main).closest('.shopify-section') || main;
  section.dispatchEvent(
    new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId: section.id ? section.id.replace('shopify-section-', '') : '' },
    })
  );

  if (produitId) {
    try {
      RecentlyViewed.addProduct(produitId);
    } catch (erreur) {
      // stockage local indisponible : sans effet sur la page
    }
  }

  // Focus : si la carte cliquée a été remplacée, il revient sur la carte de la fiche affichée.
  if (avaitFocusDansSelecteur && !main.contains(document.activeElement)) {
    const carte = main.querySelector('md-selecteur-bundle a[aria-current="page"]');
    if (carte instanceof HTMLElement) carte.focus({ preventScroll: true });
  }

  // Annonce pour les lecteurs d'écran : le titre de la fiche affichée.
  const annonce = main.querySelector('md-selecteur-bundle [data-md-annonce]');
  if (annonce && titre) annonce.textContent = titre;
}

/**
 * Charge une version et remplace <main>.
 * @param {URL} url
 * @param {{ pousser: boolean }} options - pousser : nouvelle entrée d'historique (clic) ; sinon retour arrière/avant.
 */
async function chargerVersion(url, { pousser }) {
  const jeton = ++etat.jeton;
  for (const selecteur of document.querySelectorAll('md-selecteur-bundle')) selecteur.setAttribute('aria-busy', 'true');

  try {
    const { texte, url: urlLue } = await lirePage(url);
    if (jeton !== etat.jeton) return;

    const doc = new DOMParser().parseFromString(texte, 'text/html');
    const ancienMain = document.querySelector('main');
    const nouveauMain = doc.querySelector('main');
    const nouveauFormulaire = nouveauMain?.querySelector('product-form-component');
    if (!ancienMain || !nouveauMain || !(nouveauFormulaire instanceof HTMLElement)) {
      throw new Error('fiche produit introuvable dans la page lue');
    }

    // Lectures faites avant morph (morph déplace les nœuds de la nouvelle page).
    nouveauMain.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const produitId = nouveauFormulaire.dataset.productId || '';
    const champ = nouveauFormulaire.querySelector('input[name="id"]');
    const varianteId = champ instanceof HTMLInputElement ? champ.value : '';
    const titre = (nouveauMain.querySelector('h1')?.textContent || '').trim();
    const modules = Array.from(nouveauMain.querySelectorAll('script[type="module"][src]'), (script) =>
      script.getAttribute('src')
    ).filter((source) => typeof source === 'string' && source !== '');
    const actif = document.activeElement;
    const avaitFocusDansSelecteur = Boolean(actif && actif.closest?.('md-selecteur-bundle'));

    // Adresse affichée : celle de la fiche lue (après une éventuelle redirection), paramètres conservés.
    const adresse = urlCible(urlLue);
    if (pousser) {
      history.pushState({ mdT92: true }, '', adresse.href);
    } else if (adresse.href !== window.location.href) {
      history.replaceState(history.state, '', adresse.href);
    }
    etat.chemin = adresse.pathname;

    synchroniserStyles(doc);
    morph(ancienMain, nouveauMain, OPTIONS_MORPH);
    majEntete(doc);
    chargerModules(/** @type {string[]} */ (modules));
    apresChangement({ produitId, varianteId, titre, avaitFocusDansSelecteur });
  } catch (erreur) {
    if (jeton !== etat.jeton) return;
    console.error('t92 : changement de version sans rechargement impossible, rechargement complet', erreur);
    // Repli A : la fiche s'ouvre normalement.
    if (pousser) {
      window.location.assign(url.href);
    } else {
      window.location.reload();
    }
  } finally {
    if (jeton === etat.jeton) {
      for (const selecteur of document.querySelectorAll('md-selecteur-bundle')) {
        selecteur.removeAttribute('aria-busy');
        for (const carte of selecteur.querySelectorAll('.is-pending')) carte.classList.remove('is-pending');
      }
    }
  }
}

/** Retour arrière / avant du navigateur entre des versions affichées sans rechargement. */
function ecouterHistorique() {
  if (etat.ecouteHistorique) return;
  etat.ecouteHistorique = true;

  window.addEventListener('popstate', () => {
    // Même fiche (ancre, variante) : rien à faire.
    if (window.location.pathname === etat.chemin) return;
    if (!document.querySelector('main product-form-component')) {
      window.location.reload();
      return;
    }
    chargerVersion(new URL(window.location.href), { pousser: false });
  });
}

/**
 * <md-selecteur-bundle> : cartes des versions de la gamme.
 * @extends {Component}
 */
class MdSelecteurBundle extends Component {
  /** @type {AbortController | undefined} */
  #controleur;

  connectedCallback() {
    super.connectedCallback();

    this.#controleur?.abort();
    this.#controleur = new AbortController();
    if (!navigationPossible(this)) return;

    const { signal } = this.#controleur;
    this.addEventListener('pointerover', this.#precharger, { signal, passive: true });
    this.addEventListener('focusin', this.#precharger, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controleur?.abort();
  }

  /**
   * Clic sur une carte (attribut on:click="/choisir" de chaque lien).
   * @param {MouseEvent} event
   */
  choisir(event) {
    const carte = event.target instanceof Element ? event.target.closest('a[data-md-version]') : null;
    if (!(carte instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (carte.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      return;
    }

    if (!navigationPossible(this)) return; // repli A : lien simple

    event.preventDefault();
    for (const autre of this.querySelectorAll('.is-pending')) autre.classList.remove('is-pending');
    carte.classList.add('is-pending');

    ecouterHistorique();
    chargerVersion(urlCible(carte.href), { pousser: true });
  }

  /**
   * Préchargement de la fiche au survol ou au focus d'une carte.
   * @param {Event} event
   */
  #precharger = (event) => {
    const carte = event.target instanceof Element ? event.target.closest('a[data-md-version]') : null;
    if (!(carte instanceof HTMLAnchorElement) || carte.getAttribute('aria-current') === 'page') return;
    if (/** @type {any} */ (navigator).connection?.saveData) return;
    lirePage(urlCible(carte.href)).catch(() => {
      // échec silencieux : le clic refera la demande, puis le repli A s'appliquera si besoin
    });
  };
}

if (!customElements.get('md-selecteur-bundle')) {
  customElements.define('md-selecteur-bundle', MdSelecteurBundle);
}
